import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react'
import { useToast } from '@/hooks/use-toast'

type Orientation = 'landscape' | 'portrait' | 'square'

// Novas interfaces para dados do backend
export interface SubProduct {
  id: string
  horizontal: number | null
  vertical: number | null
  orientation: Orientation
  relatedLines?: {
    id: string
    numberPages: number
    price: number
    maximumPhotos: number
  }[]
}

export interface PageOption {
  id: string
  numberPages: number
  price: number
  maximumPhotos: number
}

export interface Curatory {
  id: string
  value: number
  number_photos: number
}

export interface Addon {
  id: string
  product_id: string
  name: string
  description: string
  original_price: number
  price: number
  thumbnail_url?: string
}

export interface CartItem {
  id: string
  productId: string
  productName: string
  productImage: string

  // Campos legados (mantidos para compatibilidade)
  orientation?: Orientation
  pages?: number
  coverType?: 'fotografica' | 'linho' | 'couro' | 'couro-premium' | 'acrilico'

  // Novos campos do backend (opcionais para compatibilidade)
  selectedSubProduct?: SubProduct
  selectedPageOption?: PageOption
  selectedCuratory?: Curatory | null
  selectedAddons?: Addon[]

  // Preço e quantidade
  price: number
  quantity: number
}

export interface Coupon {
  code: string
  discount: number
  type: 'percentage' | 'fixed'
}

interface CartContextType {
  items: CartItem[]
  appliedCoupon: Coupon | null
  addItem: (item: Omit<CartItem, 'id' | 'quantity'>) => void
  removeItem: (id: string) => void
  updateQuantity: (id: string, quantity: number) => void
  clearCart: () => void
  getTotalItems: () => number
  getTotalPrice: () => number
  applyCoupon: (code: string) => boolean
  removeCoupon: () => void
  getDiscount: () => number
  getFinalPrice: () => number
}

// Cupons válidos (em produção, isso viria de um backend)
const validCoupons: Coupon[] = [
  { code: 'PRIMEIRACOMPRA', discount: 10, type: 'percentage' },
  { code: 'FOTO10', discount: 10, type: 'percentage' },
  { code: 'DESCONTO50', discount: 50, type: 'fixed' },
  { code: 'BEMVINDO', discount: 15, type: 'percentage' },
]

const CartContext = createContext<CartContextType | undefined>(undefined)

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    const savedCart = localStorage.getItem('cart-items')
    return savedCart ? JSON.parse(savedCart) : []
  })
  const [appliedCoupon, setAppliedCoupon] = useState<Coupon | null>(() => {
    const savedCoupon = localStorage.getItem('applied-coupon')
    return savedCoupon ? JSON.parse(savedCoupon) : null
  })
  const { toast } = useToast()

  // Persist cart to localStorage
  useEffect(() => {
    localStorage.setItem('cart-items', JSON.stringify(items))
  }, [items])

  // Persist coupon to localStorage
  useEffect(() => {
    if (appliedCoupon) {
      localStorage.setItem('applied-coupon', JSON.stringify(appliedCoupon))
    } else {
      localStorage.removeItem('applied-coupon')
    }
  }, [appliedCoupon])

  const addItem = (newItem: Omit<CartItem, 'id' | 'quantity'>) => {
    // Verifica se já existe um item idêntico no carrinho
    const existingItemIndex = items.findIndex(item => {
      // Comparação básica (compatibilidade com código antigo)
      const basicMatch =
        item.productId === newItem.productId &&
        item.orientation === newItem.orientation &&
        item.pages === newItem.pages &&
        item.coverType === newItem.coverType

      // Se não tiver dados do backend, usa apenas comparação básica
      if (!newItem.selectedSubProduct && !newItem.selectedPageOption) {
        return basicMatch
      }

      // Comparação avançada (novos dados do backend)
      const subProductMatch =
        item.selectedSubProduct?.id === newItem.selectedSubProduct?.id

      const pageOptionMatch =
        item.selectedPageOption?.id === newItem.selectedPageOption?.id

      const curatoryMatch =
        (!item.selectedCuratory && !newItem.selectedCuratory) ||
        item.selectedCuratory?.id === newItem.selectedCuratory?.id

      const addonsMatch = (() => {
        const itemAddonIds = (item.selectedAddons || []).map(a => a.id).sort()
        const newItemAddonIds = (newItem.selectedAddons || [])
          .map(a => a.id)
          .sort()
        return JSON.stringify(itemAddonIds) === JSON.stringify(newItemAddonIds)
      })()

      return (
        basicMatch &&
        subProductMatch &&
        pageOptionMatch &&
        curatoryMatch &&
        addonsMatch
      )
    })

    if (existingItemIndex > -1) {
      // Incrementa quantidade se item já existe
      const updatedItems = [...items]
      updatedItems[existingItemIndex].quantity += 1
      setItems(updatedItems)

      toast({
        title: 'Quantidade atualizada',
        description: `${newItem.productName} já estava no carrinho. Quantidade aumentada.`,
      })
    } else {
      // Adiciona novo item
      const timestamp = Date.now()
      const subProductId =
        newItem.selectedSubProduct?.id || newItem.orientation || 'default'
      const pageId =
        newItem.selectedPageOption?.id || newItem.pages || 'default'
      const id = `${newItem.productId}-${subProductId}-${pageId}-${timestamp}`

      setItems([...items, { ...newItem, id, quantity: 1 }])

      toast({
        title: 'Adicionado ao carrinho',
        description: `${newItem.productName} foi adicionado ao seu carrinho.`,
      })
    }
  }

  const removeItem = (id: string) => {
    setItems(items.filter(item => item.id !== id))
    toast({
      title: 'Item removido',
      description: 'O item foi removido do seu carrinho.',
    })
  }

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(id)
      return
    }
    setItems(items.map(item => (item.id === id ? { ...item, quantity } : item)))
  }

  const clearCart = () => {
    setItems([])
    setAppliedCoupon(null)
    localStorage.removeItem('cart-items')
    localStorage.removeItem('applied-coupon')

    toast({
      title: 'Carrinho limpo',
      description: 'Todos os itens foram removidos do carrinho.',
    })
  }

  const getTotalItems = () => {
    return items.reduce((total, item) => total + item.quantity, 0)
  }

  const getTotalPrice = () => {
    return items.reduce((total, item) => total + item.price * item.quantity, 0)
  }

  const applyCoupon = (code: string): boolean => {
    const coupon = validCoupons.find(
      c => c.code.toUpperCase() === code.toUpperCase(),
    )

    if (!coupon) {
      toast({
        title: 'Cupom inválido',
        description: 'O cupom informado não existe ou expirou.',
        variant: 'destructive',
      })
      return false
    }

    setAppliedCoupon(coupon)
    toast({
      title: 'Cupom aplicado!',
      description: `Você ganhou ${coupon.type === 'percentage' ? coupon.discount + '%' : 'R$ ' + coupon.discount} de desconto.`,
    })
    return true
  }

  const removeCoupon = () => {
    setAppliedCoupon(null)
    toast({
      title: 'Cupom removido',
      description: 'O cupom de desconto foi removido.',
    })
  }

  const getDiscount = (): number => {
    if (!appliedCoupon) return 0

    const total = getTotalPrice()
    if (appliedCoupon.type === 'percentage') {
      return (total * appliedCoupon.discount) / 100
    }
    return appliedCoupon.discount
  }

  const getFinalPrice = (): number => {
    return Math.max(0, getTotalPrice() - getDiscount())
  }

  return (
    <CartContext.Provider
      value={{
        items,
        appliedCoupon,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        getTotalItems,
        getTotalPrice,
        applyCoupon,
        removeCoupon,
        getDiscount,
        getFinalPrice,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

export const useCart = () => {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within CartProvider')
  }
  return context
}
